import { useNavigate, useParams , Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InvitePeek } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { Wordmark } from "../components/Wordmark";
import { authClient } from "../lib/auth";
import { Avatar, Spinner, EmptyState } from "../components/ui";
import { Cover } from "../components/BookBits";
import { GenrePills } from "../components/ClubHome";
import { useWelcomeGate } from "./Welcome";
import { SignInButtons } from "../components/SignIn";

/*
 * The invitation (PLAN.md §Join page). Being handed the link should feel
 * like being handed the club: name, who sent it, what they're reading, the
 * shelf so far, how they work, one button. A club with no history shows what
 * the app is instead of placeholders.
 */
const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const cadence = (d: number) => d < 10 ? "every week" : d < 18 ? "every two weeks" : d < 25 ? "every three weeks" : d < 40 ? "every month" : `every ${Math.round(d / 7)} weeks`;

export function Join() {
  const { code } = useParams({ from: "/join/$code" });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const session = authClient.useSession();
  useWelcomeGate(); // a brand-new account sets up its card first, then comes back here
  const peek = useQuery({ queryKey: ["invite", code], queryFn: () => api.peekInvite(code), retry: false });
  const accept = useMutation({
    mutationFn: () => api.acceptInvite(code),
    onSuccess: async ({ clubSlug }) => {
      await qc.invalidateQueries({ queryKey: ["me"] });
      void navigate({ to: "/clubs/$clubId", params: { clubId: clubSlug }, replace: true });
    },
  });
  const signOut = useMutation({ mutationFn: () => authClient.signOut() });

  if (peek.isPending) return <main className="page landing"><Spinner label="Checking your invite" /></main>;
  // Expired, revoked, used up or wrong: one message, and nothing about the club.
  if (peek.isError) return (
    <main className="page landing join-dead">
      <Wordmark size="md" />
      <EmptyState title="This invite is no longer valid"
        action={<Link to={session.data ? "/clubs" : "/"} className="btn primary">{session.data ? "Go to your clubs" : "What is Pagemates?"}</Link>}>
        Ask whoever sent it for a new one.
      </EmptyState>
    </main>
  );
  const p = peek.data;
  const dead = p.status !== "valid";
  const fresh = !p.current && p.finished.count === 0;

  const cta = dead ? (
    <div className="jrules dead">
      <span className="k">{{ expired: "This link has expired", revoked: "This link was turned off", used: "This link was already used" }[p.status as "expired" | "revoked" | "used"]}</span>
      Invites last a while, not forever. Ask whoever sent it for a fresh one; it takes them ten seconds.
    </div>
  ) : session.data ? (
    <div className="signin">
      <button className="btn primary block" disabled={accept.isPending} onClick={() => accept.mutate()}>{accept.isPending ? "Joining…" : `Join as ${session.data.user.name}`}</button>
      <p className="muted small center">Signed in as <b>{session.data.user.name}</b> · not you? <button type="button" className="linkish small" onClick={() => signOut.mutate()}>sign out</button></p>
      {accept.isError && <p className="error small center">{describeError(accept.error)}</p>}
    </div>
  ) : (
    <SignInButtons next={`/join/${code}`} verb="Continue with" disabled={session.isPending} />
  );

  return (
    <main className="page join">
      <div className="jgrid">
        <div className="jleft">
          <Wordmark size="sm" />
          <Invite p={p} dead={dead} fresh={fresh} />
          {!dead && p.current && (
            <div className="jnow">
              <Cover book={{ hardcoverId: 0, title: p.current.title, authors: p.current.authors, coverUrl: p.current.coverUrl, pages: p.current.pages, releaseYear: null }} size="lg" />
              <div style={{ minWidth: 0 }}>
                <div className="k">Reading now</div>
                <div className="t">{p.current.title}</div>
                <div className="a">{p.current.authors.join(", ")}{p.current.pages ? ` · ${p.current.pages} pages` : ""}</div>
                <div className="d">{[p.current.startedAt ? `Started ${day(p.current.startedAt)}` : null, p.nextMeetingAt ? `meeting ${day(p.nextMeetingAt)}` : null].filter(Boolean).join(" · ")}</div>
              </div>
            </div>
          )}
          {!dead && p.finished.count > 0 && (
            <div className="jshelf">
              <div className="k"><span>The shelf so far</span><span>{p.finished.count} {p.finished.count === 1 ? "book" : "books"}{p.finished.sinceYear ? ` since ${p.finished.sinceYear}` : ""}</span></div>
              <div className="jplank">
                {p.finished.covers.slice(0, 5).map((b, i) => <Cover key={i} book={{ hardcoverId: i, title: b.title, authors: [], coverUrl: b.coverUrl, pages: null, releaseYear: null }} size="sm" />)}
                {p.finished.count > 5 && <span className="cover sm more">+{p.finished.count - 5}</span>}
              </div>
            </div>
          )}
          {!dead && fresh && (
            <div className="jfresh">
              <div className="k">Just getting started</div>
              <p>No book on the shelf yet. You'll help pick the first one.</p>
              <ul>
                <li><b>See where everyone is</b> in the book, without anyone having to say.</li>
                <li><b>Talk as you read.</b> Messages past your spot stay sealed until you get there.</li>
                <li><b>Meet with an agenda</b> the club builds on the way: questions, flagged passages, your notes.</li>
              </ul>
            </div>
          )}
          {!dead && <div className="mobile-only"><Facts p={p} /></div>}
          {!dead && p.rules && <div className="jrules"><span className="k">How this club works</span>{p.rules}</div>}
          {dead && <p className="muted small"><a href="/">What is Pagemates?</a></p>}
          <div className="jcta mobile-only">{cta}</div>
        </div>
        <aside className="jright">
          {!dead && <Facts p={p} />}
          <div className="jcta">{cta}</div>
        </aside>
      </div>
    </main>
  );
}

function Invite({ p, dead, fresh }: { p: InvitePeek; dead: boolean; fresh: boolean }) {
  return (
    <div className="jinv">
      <div className="eyebrow">{dead ? "You were invited to" : "You're invited to"}</div>
      <h1 className="name">{p.clubName}</h1>
      {p.description && <p className="tag">{p.description}</p>}
      {(p.genres?.length ?? 0) > 0 && <GenrePills genres={p.genres!} />}
      {!dead && (
        <div className="by">
          {p.invitedBy && <Avatar name={p.invitedBy.name} image={p.invitedBy.image} />}
          <span>{p.invitedBy ? <><b>{p.invitedBy.name}</b> sent you this · </> : null}{p.memberCount} {p.memberCount === 1 ? "member" : "members"}{fresh ? " · just getting started" : ""}</span>
        </div>
      )}
    </div>
  );
}

function Facts({ p }: { p: InvitePeek }) {
  const rows: [string, string][] = [];
  if (p.memberNames.length > 0) rows.push(["Members", `${p.memberCount} · ${p.memberNames.join(", ")}${p.memberCount > p.memberNames.length ? "…" : ""}`]);
  if (p.usualPace) rows.push(["Pace", `about ${p.usualPace} pages a day`]);
  if (p.cadenceDays || p.nextMeetingAt) rows.push(["Meets", [p.cadenceDays ? cadence(p.cadenceDays) : null, p.nextMeetingAt ? `next ${day(p.nextMeetingAt)}` : null].filter(Boolean).join(" · ")]);
  rows.push(["Next book", { admins: "the organizer picks", vote: "picked by vote", turns: "members take turns" }[p.pickPolicy]]);
  rows.push(["Spoilers", p.spoilerGating ? "sealed by where you are" : "no sealing, read at your own risk"]);
  return (
    <div className="jfacts">
      {rows.map(([k, v]) => <div key={k} className="f"><span>{k}</span><b>{v}</b></div>)}
    </div>
  );
}
